'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@apollo/client';
import {
  Container,
  Typography,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  MenuItem,
} from '@mui/material';
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import CourseCard from ':src/components/CourseCard';
import MoGrid from ':src/components/MoGrid';
import { courseSearchLookupQuery, coursesQuery } from ':src/apollo/courses/queries';
import {
  CoursesQuery,
  CoursesQueryVariables,
} from ':src/apollo/courses/__generated__/CoursesQuery';
import Spacing from ':src/components/Spacing';
import { getPaginationParams } from ':src/utils/pagination';
import { CourseSearchLookupQuery } from ':src/apollo/courses/__generated__/CourseSearchLookupQuery';
import MoSelect from ':src/components/MoSelect';
import CourseCardSkeleton from ':src/components/CourseCardSkeleton';
import { SearchParamsType } from ':src/components/FilterDrawer';
import { pluralize } from ':src/utils/string';

const FilterDrawer = dynamic(() => import(':src/components/FilterDrawer'));

const PAGE_SIZE = 100;

const getFetchVariables = ({
  topicId,
  creditHours,
  licenseCategoryId,
  accreditationId,
}: SearchParamsType) => ({
  ...getPaginationParams(1, PAGE_SIZE),
  ...(topicId && { topicId: Number(topicId) }),
  ...(creditHours && { creditHours: Number(creditHours) }),
  ...(licenseCategoryId && { licenseCategoryId: Number(licenseCategoryId) }),
  ...(accreditationId && { accreditationId }),
});

export default function Courses() {
  const router = useRouter();
  const isMounted = useRef(false);

  const params = useSearchParams();
  const topicId = params?.get('topicId');
  const creditHours = params?.get('creditHours');
  const licenseCategoryId = params?.get('licenseCategoryId');
  const accreditationId = params?.get('accreditationId');

  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    topicId: topicId || '',
    creditHours: creditHours || '',
    licenseCategoryId: licenseCategoryId || '',
    accreditationId: accreditationId || '',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, loading, refetch } = useQuery<CoursesQuery, CoursesQueryVariables>(coursesQuery, {
    variables: getFetchVariables(searchParams),
    onCompleted: () => {
      const { topicId, creditHours, licenseCategoryId, accreditationId } = searchParams;
      const params = new URLSearchParams();
      if (topicId) params.set('topicId', topicId);
      if (creditHours) params.set('creditHours', creditHours);
      if (licenseCategoryId) params.set('licenseCategoryId', licenseCategoryId);
      if (accreditationId) params.set('accreditationId', accreditationId);

      const paramString = params.toString();
      router.replace(`/courses${paramString === '' ? '' : `?${paramString}`}`);
    },
  });

  const { data: lookupData } = useQuery<CourseSearchLookupQuery>(courseSearchLookupQuery);

  useEffect(() => {
    if (isMounted.current) {
      refetch(getFetchVariables(searchParams));
    } else {
      isMounted.current = true;
    }
  }, [searchParams, refetch]);

  const courses = data?.courses?.result;

  const topics = lookupData?.topics;
  const creditHoursOptions = lookupData?.creditHours;
  const licenseCategories = lookupData?.licenseCategories;
  const accreditations = lookupData?.credentialingBodyAccreditations;

  return (
    <>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h2">All courses</Typography>
          {isMobile && (
            <FilterDrawer
              topics={topics}
              licenseCategories={licenseCategories}
              creditHours={creditHoursOptions}
              accreditations={accreditations}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          )}
        </Box>

        <Box display={{ xs: 'none', sm: 'block' }}>
          <MoGrid container>
            <MoGrid item sm={3}>
              <MoSelect
                name="topic"
                label="Topic"
                value={searchParams.topicId || ''}
                onChange={(e) => {
                  setSearchParams({
                    ...searchParams,
                    topicId: e.target.value as string,
                  });
                }}
              >
                <MenuItem value="">Select topic</MenuItem>
                {topics?.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </MoSelect>
            </MoGrid>
            <MoGrid item sm={3}>
              <MoSelect
                name="creditHours"
                label="Credit hours"
                value={searchParams.creditHours || ''}
                onChange={(e) => {
                  setSearchParams({
                    ...searchParams,
                    creditHours: String(e.target.value),
                  });
                }}
              >
                <MenuItem value="">Select credit hours</MenuItem>
                {creditHoursOptions?.map((hours) => (
                  <MenuItem key={hours} value={hours}>
                    {hours} {pluralize('hour', hours)}
                  </MenuItem>
                ))}
              </MoSelect>
            </MoGrid>
            <MoGrid item sm={3}>
              <MoSelect
                name="licenseType"
                label="License type"
                value={searchParams.licenseCategoryId || ''}
                onChange={(e) => {
                  setSearchParams({
                    ...searchParams,
                    licenseCategoryId: e.target.value as string,
                  });
                }}
              >
                <MenuItem value="">Select license type</MenuItem>
                {licenseCategories?.map(({ id, displayName }) => (
                  <MenuItem key={id} value={id}>
                    {displayName}
                  </MenuItem>
                ))}
              </MoSelect>
            </MoGrid>
            <MoGrid item sm={3}>
              <MoSelect
                name="accreditation"
                label="Accrediting body"
                value={searchParams.accreditationId || ''}
                onChange={(e) => {
                  setSearchParams({
                    ...searchParams,
                    accreditationId: e.target.value as string,
                  });
                }}
              >
                <MenuItem value="">Select accrediting body</MenuItem>
                {accreditations?.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </MoSelect>
            </MoGrid>
          </MoGrid>
        </Box>

        <Box my={4} display={{ xs: 'none', sm: 'block' }}>
          <Divider />
        </Box>

        <Spacing y={3}>
          {loading ? (
            [1, 2, 3].map((i) => <CourseCardSkeleton key={i} />)
          ) : (
            <>
              {courses && courses.length > 0 ? (
                <>
                  {courses.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  my={6}
                >
                  <Box mb={2}>
                    <Image
                      src="/assets/search-empty.svg"
                      height={200}
                      width={360}
                      alt=""
                      unoptimized
                    />
                  </Box>
                  <Typography fontSize={24} fontWeight={600} mb={1}>
                    We didn’t find any courses.
                  </Typography>
                  <Typography>Try a broader search with less filters.</Typography>
                </Box>
              )}
            </>
          )}
        </Spacing>
      </Container>
    </>
  );
}
